import { Card, CardContent, CardHeader } from "../ui/card";

interface DistanceStatsCardProps {
  currentDistance: number | string;
  currentLabel: string;
  previousDistance: number | string;
  previousLabel: string;
  poolType: "SCY" | "SCM" | "LCM";
  timeframe: "week" | "month";
  onPoolTypeChange: (type: "SCY" | "SCM" | "LCM") => void;
  onTimeframeChange: (timeframe: "week" | "month") => void;
  changePercent?: number | null;
  formatNumber?: (n: number | string) => string;
}

export function DistanceStatsCard({
  currentDistance,
  currentLabel,
  previousDistance,
  previousLabel,
  poolType,
  timeframe,
  onPoolTypeChange,
  onTimeframeChange,
  changePercent,
  formatNumber = (n) =>
    typeof n === "number" ? n.toLocaleString() : n
}: DistanceStatsCardProps) {
  const showChange =
    typeof changePercent === "number" && !Number.isNaN(changePercent);

  return (
    <Card className="w-full">
      <CardHeader className="pb-0">
        <div className="flex justify-between items-center">
          <div className="flex w-full bg-muted/20 rounded-md overflow-hidden border border-muted">
            {(["SCY", "SCM", "LCM"] as const).map((type) => (
              <button
                key={type}
                onClick={() => onPoolTypeChange(type)}
                className={[
                  "flex-1 py-2 text-sm font-semibold transition-colors",
                  poolType === type
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted/30",
                ].join(" ")}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-4 pb-0">
        <div className="flex justify-between items-end text-center">
          <div className="flex-1">
            <div className="text-4xl font-bold text-green-600 leading-none">
              {formatNumber(currentDistance)}
            </div>

            <div className="text-sm text-muted-foreground mt-1">
              {currentLabel}
            </div>

            {showChange && (
              <div
                className={[
                  "text-xs font-medium mt-1",
                  changePercent > 0
                    ? "text-green-600"
                    : changePercent < 0
                    ? "text-red-600"
                    : "text-muted-foreground",
                ].join(" ")}
              >
                {changePercent > 0 ? "+" : ""}
                {changePercent.toFixed(1)}%
              </div>
            )}
          </div>

          <div className="flex-1">
            <div className="text-2xl font-semibold text-muted-foreground">
              {formatNumber(previousDistance)}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {previousLabel}
            </div>
          </div>
        </div>
      </CardContent>

      <div className="px-4 pb-4 mt-2">
        <div className="flex w-full rounded-md overflow-hidden">
          <button
            onClick={() => onTimeframeChange("week")}
            className={[
              "w-1/2 py-2 text-sm font-medium transition-colors border border-muted",
              timeframe === "week"
                ? "bg-primary text-primary-foreground"
                : "bg-transparent text-muted-foreground hover:bg-muted/30",
            ].join(" ")}
          >
            Weekly
          </button>

          <button
            onClick={() => onTimeframeChange("month")}
            className={[
              "w-1/2 py-2 text-sm font-medium transition-colors border-t border-b border-r border-muted",
              timeframe === "month"
                ? "bg-primary text-primary-foreground"
                : "bg-transparent text-muted-foreground hover:bg-muted/30",
            ].join(" ")}
          >
            Monthly
          </button>
        </div>
      </div>
    </Card>
  );
}
